// SLAM-only "checkpoint preview" session (plan §8.1): reticle on the ground,
// tap to place the anchor's content. No GPS/compass/PlacementProvider — the
// geo quest (M1) builds on the same engine wrapper but not on this file.
import * as THREE from 'three'
import type {RawAnchor} from '../api/types'
import type {AnchorGroup} from './anchorGroup'
import {buildAnchorGroup} from './anchorGroup'
import type {CameraStatus, EngineSession} from '../engine/engine'
import {startEngine} from '../engine/engine'

export interface ARShareCallbacks {
  onCameraStatus?: (status: CameraStatus) => void
  onAssetProgress?: (loaded: number, total: number) => void
  onReticle?: (visible: boolean) => void
  onPlaced?: () => void
  onError?: (error: unknown) => void
  /** AR ended from outside the app (WebXR: system back / "Exit AR"). */
  onSessionEnd?: () => void
}

export interface ARShareSession {
  place: () => boolean
  replace: () => void
  setTabletop: (on: boolean) => void
  recenter: () => void
  stop: () => void
}

const GROUND = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
const MIN_PLACE_DIST = 0.75
const MAX_PLACE_DIST = 12
const TABLETOP_SCALE = 0.15

export async function startARShareSession(
  canvas: HTMLCanvasElement,
  anchor: RawAnchor,
  cb: ARShareCallbacks,
): Promise<ARShareSession> {
  let engine: EngineSession | undefined
  let content: AnchorGroup | undefined
  let placed = false
  let tabletop = false
  let reticleVisible = false

  const reticle = buildReticle()
  const ray = new THREE.Ray()
  const camDir = new THREE.Vector3()
  const hit = new THREE.Vector3()

  const updateReticle = (camera: THREE.PerspectiveCamera) => {
    camera.getWorldDirection(camDir)
    ray.set(camera.position, camDir)
    const ok =
      ray.intersectPlane(GROUND, hit) !== null &&
      hit.distanceTo(camera.position) >= MIN_PLACE_DIST &&
      hit.distanceTo(camera.position) <= MAX_PLACE_DIST
    if (ok) reticle.position.copy(hit)
    reticle.visible = ok && !placed
    if (reticle.visible !== reticleVisible) {
      reticleVisible = reticle.visible
      cb.onReticle?.(reticleVisible)
    }
  }

  engine = await startEngine(canvas, {
    onCameraStatus: cb.onCameraStatus,
    onError: cb.onError,
    onSessionEnd: cb.onSessionEnd,
    onFrame: (dt, camera) => {
      updateReticle(camera)
      if (placed && content) content.tick(dt, camera.position)
    },
  })
  engine.scene.add(reticle)

  // Load content while the user is granting camera / scanning the ground.
  // If this fails, the engine is already running and must be stopped here —
  // the caller never receives a session to stop (review finding).
  try {
    content = await buildAnchorGroup(anchor, cb.onAssetProgress)
  } catch (err) {
    engine.stop()
    throw err
  }
  content.group.visible = false
  engine.scene.add(content.group)

  const applyScale = () => content?.group.scale.setScalar(tabletop ? TABLETOP_SCALE : 1)

  const fallbackPoint = new THREE.Vector3()

  // NATIVE listener, not React's onPointerDown: the engine's FullWindowCanvas
  // module re-parents the canvas to document.body, outside React 18's delegated
  // event root (#root) — React handlers on the canvas silently stop firing
  // (review finding; this was the "tap does nothing" bug on device).
  const onTap = () => {
    session.place()
  }
  canvas.addEventListener('pointerdown', onTap)

  const session: ARShareSession = {
    place: () => {
      if (!engine || !content || placed) return false
      if (reticleVisible) {
        fallbackPoint.copy(reticle.position)
      } else {
        // No ground intersection (looking up/level) — still honor the tap:
        // drop the content 2.5 m ahead of the camera on the ground plane.
        engine.camera.getWorldDirection(camDir)
        camDir.y = 0
        if (camDir.lengthSq() < 1e-4) return false
        camDir.normalize()
        fallbackPoint
          .copy(engine.camera.position)
          .addScaledVector(camDir, 2.5)
          .setY(0)
      }
      content.group.position.copy(fallbackPoint)
      // Face the user (yaw only): authored -Z content front looks at the camera.
      const cam = engine.camera.position
      content.group.rotation.y = Math.atan2(
        cam.x - content.group.position.x,
        cam.z - content.group.position.z,
      )
      applyScale()
      content.group.visible = true
      placed = true
      reticle.visible = false
      cb.onPlaced?.()
      return true
    },
    replace: () => {
      if (!content) return
      content.group.visible = false
      placed = false
    },
    setTabletop: (on: boolean) => {
      tabletop = on
      applyScale()
    },
    recenter: () => engine?.recenter(),
    stop: () => {
      canvas.removeEventListener('pointerdown', onTap)
      engine?.stop()
      content?.dispose()
    },
  }
  return session
}

function buildReticle(): THREE.Object3D {
  const group = new THREE.Group()
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(0.14, 0.18, 40),
    new THREE.MeshBasicMaterial({color: 0xffffff, transparent: true, opacity: 0.9}),
  )
  ring.rotation.x = -Math.PI / 2
  const dot = new THREE.Mesh(
    new THREE.CircleGeometry(0.03, 24),
    new THREE.MeshBasicMaterial({color: 0xffffff, transparent: true, opacity: 0.7}),
  )
  dot.rotation.x = -Math.PI / 2
  group.add(ring, dot)
  group.visible = false
  return group
}
